import React from 'react';
import { Button, Divider, Stack, Typography } from '@mui/material';

import { MaterialPropertyValuePair } from '@paragon/client-tools';
import { DistanceField, NumericField } from '@paragon/material';

import {
  changeInsertionTrussEnvelopeComponentType,
  changeInsertionTrussEnvelopeDefinitionType,
  editInsertionTrussEnvelope,
  finishInsertingTrussEnvelope,
  recalculateInsertionTrussEnvelope,
} from '../../../../../store/actions/layout/trussEnvelopeActions';
import { isLayoutBasedOnRoofContainers } from '../../../../../store/selectors/layoutModeSelectors';
import { getTrussEnvelopeInsertionInformation } from '../../../../../store/selectors/trussEnvelopeInsertionInformationSelectors';
import { setInsertionTrussEnvelopeProperties } from '../../../../../store/slices/applicationSlice';
import { FeaturePatternContainerSelect } from '../../../../controls/FeaturePatternContainerSelect';
import { RoofContainerSelect } from '../../../../controls/RoofContainerSelect';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { EnvelopeJustificationToggle } from '../../elements/EnvelopeJustificationToggle';
import { RecalculateButton } from '../../elements/RecalculateButton';
import { ProjectHierarchyContextSelects } from '../../properties/ProjectHierarchyContextSelects';
import { TrussEnvelopeBevelCutTypeSelect } from '../../properties/TrussEnvelopeBevelCutTypeSelect';
import { TrussEnvelopeEndConnectionTypeSelect } from '../../properties/TrussEnvelopeEndConnectionTypeSelect';
import { TrussEnvelopeLabelPlacementSelect } from '../../properties/TrussEnvelopeLabelPlacementSelect';
import InsertionPanelContainer from '../InsertionPanelContainer';
import { TrussDefinitionTypeSelect } from './TrussDefinitionTypeSelect';
import { TrussEnvelopeAdditionalCutsProperties } from './TrussEnvelopeAdditionalCutsProperties';
import { TrussEnvelopeInsertionPointControls } from './TrussEnvelopeInsertionPointControls';
import { TrussTypeSelect } from './TrussTypeSelect';

export function TrussEnvelopeInsertionSubPanel() {
  const dispatch = useAppDispatch();

  const insertion = useAppSelector(getTrussEnvelopeInsertionInformation);
  const inRoofContainerProject = useAppSelector(isLayoutBasedOnRoofContainers);

  if (insertion === null) return null;

  const needsContainerSelection =
    insertion.data.definitionType === 'FeaturePattern' ||
    (insertion.data.definitionType === 'Profile' && inRoofContainerProject);

  const readyToInsert = insertion.isInsertionEnvelopeFullyDefined();
  const bothPointsChosen = !!(insertion.data.leftPoint && insertion.data.rightPoint);

  return (
    <InsertionPanelContainer
      disabled={!readyToInsert}
      onInsert={async () => {
        await dispatch(finishInsertingTrussEnvelope());
      }}
      // TODO: layout
      additionalButtons={
        <>
          <Button
            variant="outlined"
            sx={{ m: 1, py: 1 }}
            disabled={!readyToInsert}
            onClick={async () => {
              await dispatch(finishInsertingTrussEnvelope({ postInsertionAction: 'repeat' }));
            }}
          >
            Insert and Repeat
          </Button>

          <Button
            variant="outlined"
            sx={{ m: 1, py: 1 }}
            disabled={!readyToInsert}
            onClick={async () => {
              await dispatch(finishInsertingTrussEnvelope({ postInsertionAction: 'start-run' }));
            }}
          >
            Insert and Start Run
          </Button>
        </>
      }
    >
      <div className="clickable-during-picking">
        <Typography align="center" variant="h5" sx={{ marginBottom: 3 }}>
          Truss Envelope Creation
        </Typography>

        <ProjectHierarchyContextSelects
          levelGuid={insertion.data.levelGuid}
          onChange={(levelGuid) => dispatch(setInsertionTrussEnvelopeProperties({ levelGuid }))}
        />

        <Stack spacing={2} sx={{ marginTop: 2 }}>
          <Stack direction="row" spacing={1}>
            <TrussTypeSelect
              value={insertion.data.componentType}
              onChange={(componentType) => {
                dispatch(changeInsertionTrussEnvelopeComponentType(componentType));
              }}
            />

            <TrussDefinitionTypeSelect
              value={insertion.data.definitionType}
              trussType={insertion.data.componentType}
              onChange={(definitionType) => {
                dispatch(changeInsertionTrussEnvelopeDefinitionType(definitionType));
              }}
            />
          </Stack>

          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <RecalculateButton
              disabled={!readyToInsert}
              onRecalculate={(end) => dispatch(recalculateInsertionTrussEnvelope(end))}
            />
          </Stack>

          <Divider />

          {needsContainerSelection &&
            (insertion.data.definitionType === 'Profile' ? (
              <RoofContainerSelect
                roofContainerGuid={insertion.data.roofContainerGuid}
                onChange={(roofContainerGuid) => {
                  dispatch(setInsertionTrussEnvelopeProperties({ roofContainerGuid }));
                }}
              />
            ) : insertion.data.definitionType === 'FeaturePattern' ? (
              insertion.isFeaturePatternContainerInferenceActive() ? (
                <MaterialPropertyValuePair property="Feature Pattern Container" value="<pending>" />
              ) : (
                <FeaturePatternContainerSelect
                  featurePatternContainerGuid={insertion.data.featurePatternContainerGuid}
                  onChange={(featurePatternContainerGuid) => {
                    dispatch(
                      setInsertionTrussEnvelopeProperties({ featurePatternContainerGuid, roofContainerGuid: null }),
                    );
                  }}
                  levelGuidFilter={insertion.data.levelGuid}
                />
              )
            ) : null)}

          <EnvelopeJustificationToggle
            value={insertion.data.justification}
            onChange={(justification) => {
              dispatch(editInsertionTrussEnvelope({ justification }));
            }}
          />

          <Stack direction="row" spacing={1}>
            <DistanceField
              sx={{ marginBottom: 0 }}
              label="Thickness"
              min={1 / 16}
              value={insertion.data.thickness}
              onSubmit={(thickness) => dispatch(editInsertionTrussEnvelope({ thickness }))}
            />

            <NumericField
              sx={{ marginBottom: 0 }}
              min={1}
              integers
              label="Plies"
              value={insertion.data.numberOfPlies}
              onSubmit={(numberOfPlies) => {
                dispatch(editInsertionTrussEnvelope({ numberOfPlies }));
              }}
            />
          </Stack>

          <Divider />

          <TrussEnvelopeInsertionPointControls insertion={insertion} bothPointsChosen={bothPointsChosen} />

          {insertion.data.componentType === 'RoofTruss' && (
            <>
              <Divider />

              <Stack direction="row" spacing={1}>
                <DistanceField
                  label="Left Overhang"
                  min={0}
                  canBeNull
                  value={insertion.data.leftOverhang ?? null}
                  onSubmit={(leftOverhang) => dispatch(editInsertionTrussEnvelope({ leftOverhang }))}
                  disabled={!readyToInsert}
                />

                <DistanceField
                  label="Right Overhang"
                  min={0}
                  canBeNull
                  value={insertion.data.rightOverhang ?? null}
                  onSubmit={(rightOverhang) => dispatch(editInsertionTrussEnvelope({ rightOverhang }))}
                  disabled={!readyToInsert}
                />
              </Stack>
            </>
          )}

          {insertion.data.definitionType === 'Profile' && (
            <>
              <Divider />

              <Typography variant="h6" gutterBottom>
                Additional Cuts
              </Typography>

              <Stack spacing={2}>
                <TrussEnvelopeBevelCutTypeSelect
                  name="Left Bevel Cut"
                  disabled={!readyToInsert}
                  bevelCut={insertion.data.leftBevelCut}
                  submit={(leftBevelCut) => dispatch(editInsertionTrussEnvelope({}, { leftBevelCut }))}
                />

                <TrussEnvelopeBevelCutTypeSelect
                  name="Right Bevel Cut"
                  disabled={!readyToInsert}
                  bevelCut={insertion.data.rightBevelCut}
                  submit={(rightBevelCut) => dispatch(editInsertionTrussEnvelope({}, { rightBevelCut }))}
                />

                <TrussEnvelopeEndConnectionTypeSelect
                  name="Left Carried Chords"
                  connectionType={insertion.data.leftEndConnectionOverride}
                  submit={(leftEndConnectionOverride) =>
                    dispatch(setInsertionTrussEnvelopeProperties({ leftEndConnectionOverride }))
                  }
                />

                <TrussEnvelopeEndConnectionTypeSelect
                  name="Right Carried Chords"
                  connectionType={insertion.data.rightEndConnectionOverride}
                  submit={(rightEndConnectionOverride) =>
                    dispatch(setInsertionTrussEnvelopeProperties({ rightEndConnectionOverride }))
                  }
                />

                <TrussEnvelopeLabelPlacementSelect
                  labelPlacement={insertion.data.labelPlacement}
                  submit={(labelPlacement) => dispatch(setInsertionTrussEnvelopeProperties({ labelPlacement }))}
                />

                <TrussEnvelopeAdditionalCutsProperties
                  hideTitle
                  topCuts={insertion.data.topCuts}
                  bottomCuts={insertion.data.bottomCuts}
                  targetInfo={{ target: 'insertionTrussEnvelope' }}
                />
              </Stack>
            </>
          )}
        </Stack>
      </div>
    </InsertionPanelContainer>
  );
}
